import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { LanguageService } from 'src/app/@shared/services/language/language.service';
import { Direction } from '@angular/cdk/bidi';
import {
  CHOOSE_LANGUAGE,
  CLOSE,
  CLOSE_MENU,
  CONTACT_US,
  FOR,
  MENU,
  NO_RESULT_FOUND,
  SEARCH,
  SUBMIT_COMPLAIN,
} from 'src/app/modules/route-planner/constants/multi-language.constants';
import { environment } from 'src/environments/environment';
import { BehaviorSubject, Observable, Subject, of } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  switchMap,
  takeUntil,
} from 'rxjs/operators';
import {
  WebsiteCmsSearchService,
  WebsiteCmsSearchResultItem,
  buildMotWebsitePageUrl,
} from 'src/app/@shared/services/website-cms-search/website-cms-search.service';

export interface MenuItem {
  title: string;
  url: string;
  children?: MenuItem[];
}

export interface ApiMenuItem {
  iconUrl: string | null;
  id: number;
  orderIndex: number;
  parentId: number | null;
  children: ApiMenuItem[];
  title: string;
  url: string;
  isVisible: boolean;
  uploadedFileReferenceId: string | null;
  languageId: number;
}

@Component({
    selector: 'app-side-menu',
    templateUrl: './side-menu.component.html',
    styleUrl: './side-menu.component.scss',
    standalone: false
})


export class SideMenuComponent implements OnInit, OnChanges, OnDestroy {
  @Input() isOpen = false;
  @Output() close = new EventEmitter<void>();
  @Output() openContact = new EventEmitter<void>();
  @Output() openComplaint = new EventEmitter<void>();

  /**
   * a11y: ref to the main sidebar close (chevron + "תפריט") button.
   * Audit: "בלחיצה על ההמבורגר יש להקפיץ את הפוקוס לכפתור המסומן בתמונה,
   * כפתור סגירת התפריט" — focus must jump to this button the moment the
   * sidebar opens (mirrors mot-website-client/sidebar behaviour).
   */
  @ViewChild('closeBtn', { static: false }) closeBtnRef?: ElementRef<HTMLButtonElement>;

  /**
   * a11y: refs for language-picker focus management.
   * Audit asks that focus jump to the X (close) button the instant the
   * "Choose language" dialog opens, and return to the trigger on close.
   * Mirrors the implementation in mot-website-client sidebar.
   */
  @ViewChild('languageModalCloseBtn', { static: false }) languageModalCloseBtnRef?: ElementRef<HTMLButtonElement>;
  @ViewChild('languageToggleBtn', { static: false }) languageToggleBtnRef?: ElementRef<HTMLButtonElement>;

  searchQuery = '';
  isSearchActive = false;
  private readonly searchQuerySubject = new BehaviorSubject<string>('');
  searchResults$!: Observable<WebsiteCmsSearchResultItem[]>;
  private readonly destroy$ = new Subject<void>();

  // Submenu management
  openSubmenus: Set<number> = new Set();
  menuItems: MenuItem[] = [];
  currentLanguageLabel: string
  direction: Direction
  menuText: string;
  searchText: string;
  makeComplaintText: string;
  contactUsText: string;
  chooseLanguageText: string;
  noResultsText: string;
  exitSearchText: string;
  closeText: string;
  /**
   * a11y: accessible name for the main sidebar back/close button — per audit
   * it must read "סגירת תפריט" (not the generic "סגור"), so it lives in its
   * own field separate from the generic `closeText` used by other X buttons.
   */
  closeMenuText: string = '';

  // Language selector
  isLanguageOpen = false;
  currentLanguage: string = 'he';
  languages = [
    { code: 'he' as const, label: 'עברית' },
    { code: 'en' as const, label: 'English' },
    { code: 'ar' as const, label: 'عربي' }
  ];

  /**
   * a11y: external complaint form URL used by the "הגשת פנייה" link in the
   * sidebar footer. Sourced from environment so dev/staging/prod can override.
   */
  readonly complaintUrl: string = environment.complaintUrl;



  constructor(
    private readonly languageService: LanguageService,
    private readonly http: HttpClient,
    private readonly websiteCmsSearch: WebsiteCmsSearchService
  ) {}

  ngOnInit(): void {
    // Subscribe to locale changes
    // this.localeService.currentLocale$.subscribe(locale => {
    //   this.currentLanguage = locale;
    // });
    this.languageService.selectedLanguage$.subscribe((language) => {
      this.fetchMenuItems(language.code);
      this.currentLanguageLabel = language.label;
      this.currentLanguage = language.symbol;
      this.direction = language.direction;
      this.contactUsText = CONTACT_US[language.language];
      this.makeComplaintText = SUBMIT_COMPLAIN[language.language];
      this.searchText = SEARCH[language.language];
      this.menuText = MENU[language.language];
      this.chooseLanguageText = CHOOSE_LANGUAGE[language.language];
      this.noResultsText = `${NO_RESULT_FOUND[language.language]} ${FOR[language.language]}`;
      this.exitSearchText = CLOSE[language.language];
      this.closeText = CLOSE[language.language];
      this.closeMenuText = CLOSE_MENU[language.language];
    });

    this.searchResults$ = this.searchQuerySubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((query) => {
        if (!query || query.trim().length < 2) {
          return of([]);
        }
        const languageId = this.languageService.getSelectedLanguage().code;
        return this.websiteCmsSearch.search(languageId, query.trim());
      }),
      takeUntil(this.destroy$)
    );
  }

  /**
   * a11y: when the host toggles `isOpen` from false → true (hamburger click),
   * push keyboard focus onto the sidebar's main close button so screen-reader
   * users land *inside* the just-opened menu. Matches the parallel
   * mot-website-client/sidebar behaviour.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen'] && this.isOpen) {
      this.focusCloseButton();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Defer to next tick so the `[attr.inert]` toggle and animation classes
   * have actually applied and the button is focusable.
   */
  private focusCloseButton(): void {
    setTimeout(() => {
      this.closeBtnRef?.nativeElement?.focus();
    }, 0);
  }



  /**
   * Fetch menu items from API
   */
  private fetchMenuItems(languageId: number): void {
    const apiUrl = environment.bo_be_service_url + 'menu/language/' + languageId + '/detailed';

    this.http.get<ApiMenuItem[]>(apiUrl).subscribe({
      next: (response) => {
        this.menuItems = this.mapApiResponseToMenuItems(response);
      },
      error: (error) => {
        console.error('Error fetching menu items:', error);
        // Set empty array as fallback
        this.menuItems = [];
      }
    });
  }

  /**
   * Map API response to MenuItem array
   */
  private mapApiResponseToMenuItems(apiItems: ApiMenuItem[]): MenuItem[] {
    return apiItems
      .filter(item => item.isVisible && item.parentId === null) // Only top-level visible items
      .sort((a, b) => a.orderIndex - b.orderIndex) // Sort by orderIndex
      .map(item => this.mapApiItemToMenuItem(item));
  }

  /**
   * Map single API item to MenuItem
   */
  private mapApiItemToMenuItem(apiItem: ApiMenuItem): MenuItem {
    const menuItem: MenuItem = {
      title: apiItem.title,
      url: apiItem.url,
      children: []
    };

    // Map children if they exist
    if (apiItem.children && apiItem.children.length > 0) {
      menuItem.children = apiItem.children
        .filter(child => child.isVisible)
        .sort((a, b) => a.orderIndex - b.orderIndex)
        .map(child => this.mapApiItemToMenuItem(child));
    }

    return menuItem;
  }

  /**
   * Get translation for a key
   */


  /**
   * Close sidebar
   */
  onClose(): void {
    this.close.emit();
  }

  /**
   * Toggle language dropdown.
   * a11y: when opening, defer focus into the dialog's close (X) button so
   * the user lands inside the modal and SR announces the dialog name.
   */
  toggleLanguage(): void {
    this.isLanguageOpen = !this.isLanguageOpen;
    if (this.isLanguageOpen) {
      this.focusLanguageModalClose();
    }
  }

  /**
   * Select language.
   * a11y: if the user picked the already-active language, return focus to
   * the language toggle button (no locale change → no view refresh).
   */
  selectLanguage(code: any): void {
    const previousSymbol = this.currentLanguage;
    this.languageService.changeLanguageBySymbol(code);
    this.isLanguageOpen = false;
    if (previousSymbol === code) {
      this.restoreFocusToLanguageToggle();
    }
  }

  /**
   * Close language modal and restore focus to the trigger.
   */
  closeLanguageModal(): void {
    if (!this.isLanguageOpen) {
      return;
    }
    this.isLanguageOpen = false;
    this.restoreFocusToLanguageToggle();
  }

  /**
   * Escape inside the language dialog closes it (per WAI-ARIA dialog pattern).
   * Angular's strict template type-checking infers `Event` (not `KeyboardEvent`)
   * for `(keydown.escape)` bindings, so we type the param accordingly.
   */
  onLanguageModalEscape(event: Event): void {
    event.stopPropagation();
    this.closeLanguageModal();
  }

  /**
   * a11y: move keyboard focus to the dialog's close (X) button on open.
   * setTimeout(0) waits for *ngIf/@if to render the button into the DOM.
   */
  private focusLanguageModalClose(): void {
    setTimeout(() => {
      this.languageModalCloseBtnRef?.nativeElement?.focus();
    }, 0);
  }

  /**
   * a11y: return focus to the language toggle that opened the dialog.
   */
  private restoreFocusToLanguageToggle(): void {
    setTimeout(() => {
      this.languageToggleBtnRef?.nativeElement?.focus();
    }, 0);
  }

  /**
   * Toggle search mode
   */
  toggleSearch(): void {
    this.isSearchActive = !this.isSearchActive;
    if (!this.isSearchActive) {
      this.searchQuery = '';
      this.searchQuerySubject.next('');
    }
  }

  exitSearch(): void {
    this.isSearchActive = false;
    this.searchQuery = '';
    this.searchQuerySubject.next('');
  }

  onSearchInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.searchQuery = target.value;
    this.searchQuerySubject.next(this.searchQuery);
  }

  onSubmitSearch(): void {
    if (this.searchQuery.trim().length < 2) {
      return;
    }
    const q = this.searchQuery.trim();
    const symbol = this.languageService.getSelectedLanguage().symbol;
    this.websiteCmsSearch.openFullSearchInNewWindow(q, symbol);
    this.exitSearch();
    this.onClose();
  }

  /**
   * a11y: build the absolute href for a CMS search result.
   * Audit: "התוצאות חיפוש מוגדרות כפתור — יש לשנות את הגדרת הרכיבים לקישור
   * (הURL משתנה)" — clicking a result navigates to an external CMS page,
   * so each result must be a real `<a [href]>` (with target=_blank), not a
   * button. This getter mirrors `WebsiteCmsSearchService.openPageResultIn-
   * NewWindow` but returns the URL so the template can bind it to [href].
   */
  searchResultHref(result: WebsiteCmsSearchResultItem): string {
    const symbol = this.languageService.getSelectedLanguage().symbol;
    return buildMotWebsitePageUrl(
      environment.website_url,
      result.urlSlug,
      symbol
    );
  }

  /**
   * Close the search panel and the sidebar after the user clicks a result.
   * Navigation itself is handled natively by the anchor tag (target=_blank).
   */
  onSearchResultLinkClick(): void {
    this.exitSearch();
    this.onClose();
  }

  /**
   * Toggle submenu
   */
  toggleSubmenu(index: number): void {
    if (this.openSubmenus.has(index)) {
      this.openSubmenus.delete(index);
    } else {
      this.openSubmenus.add(index);
    }
  }

  onSubmenuTriggerKeydown(event: KeyboardEvent, index: number): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.toggleSubmenu(index);
    }
  }

  isSubmenuOpen(index: number): boolean {
    return this.openSubmenus.has(index);
  }

  submenuPanelId(index: number): string {
    return `sidebar-submenu-${index}`;
  }

  /**
   * Handle contact button click
   */
  onContactClick(): void {
    this.onClose();
    this.openContact.emit();
  }

  /**
   * a11y: build the absolute href for a CMS menu entry.
   * Audit (route-planner sidebar):
   *   - "אין כלל הגדרה על הרכיב אודות" — top-level leaf items (like "אודות"
   *     / About) were rendered as `<a (click)>` without an `href`, so they
   *     were not announced as links and were unreachable by SR users.
   *   - "יש להגדיר את הרכיבים הנמצאים בתת תפריט כקישורים" — submenu items
   *     under expandable parents must also be real links.
   * Both cases now bind this getter to `[href]`, and the click handler
   * only tears down our own UI (onClose) — navigation is performed by the
   * browser natively (with target=_blank / new tab semantics preserved).
   */
  menuItemHref(url: string): string {
    return environment.website_url + url;
  }


  private loadDemoMenuItems(): void {
    const demoData: ApiMenuItem[] = [
      {
        "iconUrl": null,
        "id": 9,
        "orderIndex": 0,
        "parentId": 10,
        "children": [],
        "title": "אוטובוסים",
        "url": "/buses",
        "isVisible": true,
        "uploadedFileReferenceId": null,
        "languageId": 1
      },
      {
        "iconUrl": null,
        "id": 6,
        "orderIndex": 1,
        "parentId": null,
        "children": [],
        "title": "דף הבית",
        "url": "/home",
        "isVisible": true,
        "uploadedFileReferenceId": null,
        "languageId": 1
      },
      {
        "iconUrl": null,
        "id": 7,
        "orderIndex": 2,
        "parentId": null,
        "children": [],
        "title": "שאלות נפוצות",
        "url": "/faq",
        "isVisible": true,
        "uploadedFileReferenceId": null,
        "languageId": 1
      },
      {
        "iconUrl": null,
        "id": 8,
        "orderIndex": 3,
        "parentId": null,
        "children": [],
        "title": "שירותים",
        "url": "/services",
        "isVisible": true,
        "uploadedFileReferenceId": null,
        "languageId": 1
      },
      {
        "iconUrl": null,
        "id": 21,
        "orderIndex": 4,
        "parentId": null,
        "children": [],
        "title": "תסת",
        "url": "/test",
        "isVisible": true,
        "uploadedFileReferenceId": null,
        "languageId": 1
      },
      {
        "iconUrl": null,
        "id": 10,
        "orderIndex": 5,
        "parentId": null,
        "children": [
          {
            "iconUrl": null,
            "id": 9,
            "orderIndex": 0,
            "parentId": 10,
            "children": [],
            "title": "אוטובוסים",
            "url": "/buses",
            "isVisible": true,
            "uploadedFileReferenceId": null,
            "languageId": 1
          }
        ],
        "title": "מונית",
        "url": "/taxi",
        "isVisible": true,
        "uploadedFileReferenceId": null,
        "languageId": 1
      },
      {
        "iconUrl": null,
        "id": 23,
        "orderIndex": 6,
        "parentId": null,
        "children": [],
        "title": "בדיקה",
        "url": "google.com",
        "isVisible": true,
        "uploadedFileReferenceId": null,
        "languageId": 1
      },
      {
        "iconUrl": null,
        "id": 24,
        "orderIndex": 7,
        "parentId": null,
        "children": [],
        "title": "taxicalculator",
        "url": "/taxicalculator",
        "isVisible": true,
        "uploadedFileReferenceId": null,
        "languageId": 1
      }
    ];

    this.menuItems = this.mapApiResponseToMenuItems(demoData);
  }

}
